<template>
  <div
    v-if="!error && !hasSummary"
    class="microlearning-view-edit microlearning-view-edit--video-only"
  >
    <div class="microlearning-view-edit__video-container">
      <div v-if="isIframeEmbed" :key="iframeKey" class="microlearning-view-edit__iframe-container">
        <iframe v-if="iframeAttrs" v-bind="iframeAttrs"></iframe>
        <p v-else role="alert" class="microlearning-view-edit__embed-error">
          {{ embedErrorMessage }}
        </p>
      </div>
      <video v-else ref="videoPlayer" controls class="microlearning-view-edit__video">
        <source :src="video" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  </div>

  <div
    v-else-if="error"
    class="microlearning-view-edit microlearning-view-edit--with-error microlearning-view-edit--error-layout-row"
  >
    <div v-if="!hideVideo" class="microlearning-view-edit__video-container">
      <div v-if="isIframeEmbed" :key="iframeKey" class="microlearning-view-edit__iframe-container">
        <iframe v-if="iframeAttrs" v-bind="iframeAttrs"></iframe>
        <p v-else role="alert" class="microlearning-view-edit__embed-error">
          {{ embedErrorMessage }}
        </p>
      </div>
      <video v-else ref="videoPlayer" controls class="microlearning-view-edit__video">
        <source :src="video" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
    <div class="microlearning-view-edit__error-column">
      <div v-if="$slots['error-actions']" class="microlearning-view-edit__error-actions">
        <slot name="error-actions" />
      </div>
      <VideoInstructionsSummary
        :reasonForInvalid="error"
        :invalidTitle="invalidTitle"
        :hideIcon="hideInvalidIcon"
        class="microlearning-view-edit__error"
      />
    </div>
  </div>

  <div
    v-else
    class="microlearning-view-edit microlearning-view-edit--with-instructions"
    :class="{
      'microlearning-view-edit--editable': editable,
      'microlearning-view-edit--compact': compactStepsLayout,
    }"
  >
    <div
      class="microlearning-view-edit__top-row"
      :class="{ 'microlearning-view-edit__top-row--compact': compactStepsLayout }"
    >
      <div v-if="!hideVideo" ref="videoWrapper" class="microlearning-view-edit__video-wrapper">
        <div
          v-if="isIframeEmbed"
          :key="iframeKey"
          class="microlearning-view-edit__iframe-container"
        >
          <iframe v-if="iframeAttrs" v-bind="iframeAttrs"></iframe>
          <p v-else role="alert" class="microlearning-view-edit__embed-error">
            {{ embedErrorMessage }}
          </p>
        </div>
        <video v-else ref="videoPlayer" controls class="microlearning-view-edit__video">
          <source :src="video" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
      <VideoInstructionsSummary
        :rubric="summary"
        :editable="editable"
        :saving="summarySaving"
        class="microlearning-view-edit__summary"
        :class="{
          'microlearning-view-edit__summary--interactive': editable,
          'microlearning-view-edit__summary--static': !editable,
        }"
        @edit="handleSummaryClick"
        @save="handleSummarySave"
      />
    </div>

    <div class="microlearning-view-edit__steps-container" :style="stepsContainerStyle">
      <div v-if="showSteps" class="microlearning-view-edit__steps-list">
        <InstructionStepCard
          v-for="(step, index) in steps"
          :key="step.id || index"
          :competence_category="step.competence_category"
          :inspector_action="step.inspector_action"
          :look_for="step.look_for"
          :pass_criteria="step.pass_criteria"
          :fail_criteria="step.fail_criteria"
          :timestamp_ref="step.timestamp_ref"
          :video_jump_url="step.video_jump_url"
          class="microlearning-view-edit__step-card"
          :class="{
            'microlearning-view-edit__step-card--interactive': editable,
            'microlearning-view-edit__step-card--static': !editable,
          }"
          @card-click="handleStepClick(step, index)"
          @timestamp-click="handleTimestampClick"
        />
      </div>
      <div v-else class="microlearning-view-edit__steps-list">
        <MicrolearningChapterCard
          v-for="(step, index) in steps"
          :key="step.id || index"
          :chapter_title="step.chapter_title || ''"
          :chapter_description="step.chapter_description || ''"
          :chapter_summary="step.chapter_summary || ''"
          :start_time="step.start_time || ''"
          :end_time="step.end_time || ''"
          :title="step.inspector_action || ''"
          :description="step.look_for || step.pass_criteria || ''"
          :timestamp_ref="step.timestamp_ref || ''"
          :video_jump_url="step.video_jump_url || ''"
          :read-only="!editable"
          :saving="chapterSavingIndex === index"
          @card-click="handleStepClick(step, index)"
          @timestamp-click="handleTimestampClick"
          @save="handleChapterSave(step, index, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import VideoInstructionsSummary from "@components/VideoInstructionsSummary/VideoInstructionsSummary.vue";
import InstructionStepCard from "@components/InstructionStepCard/InstructionStepCard.vue";
import MicrolearningChapterCard from "@components/MicrolearningChapterCard/MicrolearningChapterCard.vue";
import {
  EMBED_ALLOW,
  EMBED_SANDBOX,
  parseEmbedSource,
  withAutoplay,
} from "@components/utils/microlearningEmbed";

// Cloudflare Stream playback URL (customer subdomain, signed or unsigned) — must render as an iframe, not a <video> src.
function isCloudflareStreamUrl(value) {
  if (!value || typeof value !== "string") return false;
  return value.includes("cloudflarestream.com") || value.includes("videodelivery.net");
}

const props = defineProps({
  video: {
    type: String,
    required: true,
  },
  error: {
    type: String,
    default: null,
  },
  summary: {
    type: Object,
    default: null,
  },
  steps: {
    type: Array,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: false,
  },
  maxStepsHeight: {
    type: String,
    default: "400px",
  },
  compactStepsLayout: {
    type: Boolean,
    default: false,
  },
  hideVideo: {
    type: Boolean,
    default: false,
  },
  showSteps: {
    type: Boolean,
    default: true,
  },
  invalidTitle: {
    type: String,
    default:
      "There is a critical mismatch between the provided business context and the visual content of the video.",
  },
  hideInvalidIcon: {
    type: Boolean,
    default: false,
  },
  summarySaving: {
    type: Boolean,
    default: false,
  },
  /** Index of chapter currently saving, or -1. */
  chapterSavingIndex: {
    type: Number,
    default: -1,
  },
});

const emit = defineEmits([
  "summary-click",
  "step-click",
  "summary-save",
  "chapter-save",
  "save",
  "discard",
]);

const videoPlayer = ref(null);
const videoWrapper = ref(null);

// Track current video URL for timestamp jumps
const currentVideoUrl = ref(null);

// Key to force iframe re-render when URL changes
const iframeKey = ref(0);

const hasSummary = computed(() => {
  return props.summary && Object.keys(props.summary).length > 0;
});

// Computed style for steps container with dynamic max-height
const stepsContainerStyle = computed(() => ({
  maxHeight: props.maxStepsHeight,
}));

// Check if video is an api.video embed URL, a Cloudflare Stream URL, or iframe HTML
const isIframeEmbed = computed(() => {
  if (!props.video) return false;
  // Check for iframe HTML
  if (props.video.includes("<iframe")) {
    return true;
  }
  // Check for api.video embed URLs
  if (props.video.includes("embed.api.video") || props.video.includes("api.video/vod")) {
    return true;
  }
  // Check for Cloudflare Stream playback URLs (signed URLs contain "/iframe" in the path,
  // which must not be confused with a bare "iframe" substring match)
  if (isCloudflareStreamUrl(props.video)) {
    return true;
  }
  return false;
});

// Track if we should autoplay (when timestamp clicked)
const shouldAutoplay = ref(false);

const embedErrorMessage =
  "This video can't be displayed because its link isn't from a supported video provider.";

// The stored value is untrusted: it is reduced to one validated https src and the iframe is built
// from fixed attributes, so no stored markup is ever rendered (no v-html).
const embedSrc = computed(() => {
  if (!isIframeEmbed.value) return null;

  // Prefer the current video URL (for timestamp jumps); an unsupported one falls back to the prop
  const jumpSrc = parseEmbedSource(currentVideoUrl.value);
  const src = jumpSrc || parseEmbedSource(props.video);
  if (!src) return null;

  // Autoplay applies only to bare video URLs, not to stored iframe snippets
  if (shouldAutoplay.value && !props.video.includes("<iframe")) {
    return withAutoplay(src);
  }
  return src;
});

const iframeAttrs = computed(() => {
  if (!embedSrc.value) return null;
  return {
    src: embedSrc.value,
    sandbox: EMBED_SANDBOX,
    allow: EMBED_ALLOW,
    allowfullscreen: true,
    title: "Microlearning video",
  };
});

const handleSummaryClick = () => {
  if (props.editable) {
    emit("summary-click", props.summary);
  }
};

const handleSummarySave = (payload) => {
  if (!props.editable) return;
  emit("summary-save", payload);
};

const handleStepClick = (step, index) => {
  if (props.editable) {
    emit("step-click", { step, index });
  }
};

const handleChapterSave = (step, index, payload) => {
  if (!props.editable) return;
  emit("chapter-save", { step, index, ...payload });
};

// Handle timestamp click to jump the inline video player
const handleTimestampClick = (videoJumpUrl) => {
  if (!videoJumpUrl) return;

  shouldAutoplay.value = true;
  currentVideoUrl.value = videoJumpUrl;
  iframeKey.value++;

  if (videoWrapper.value?.scrollIntoView) {
    videoWrapper.value.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

defineExpose({
  videoPlayer,
  handleTimestampClick,
});
</script>

<style scoped>
.microlearning-view-edit {
  display: flex;
  flex-direction: column;
  gap: 22px;
  width: 100%;
}

.microlearning-view-edit__video-container {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.microlearning-view-edit__video-wrapper {
  flex: 1;
  min-width: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.microlearning-view-edit__video {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  aspect-ratio: 571 / 333;
  object-fit: cover;
}

.microlearning-view-edit__iframe-container {
  width: 100%;
  aspect-ratio: 571 / 333;
}

.microlearning-view-edit__iframe-container :deep(iframe) {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

.microlearning-view-edit__embed-error {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 16px;
  border: 1px solid #fda29b;
  border-radius: 8px;
  background: #fef3f2;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.microlearning-view-edit__error {
  width: 100%;
}

.microlearning-view-edit__top-row {
  display: flex;
  gap: 22px;
  align-items: stretch;
  width: 100%;
}

.microlearning-view-edit__top-row .microlearning-view-edit__video-wrapper {
  flex: 1;
  max-width: calc(50% - 11px);
  display: flex;
  flex-direction: column;
}

.microlearning-view-edit__top-row .microlearning-view-edit__iframe-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.microlearning-view-edit__summary {
  flex: 1;
  min-width: 0;
  max-width: calc(50% - 11px);
  display: flex;
}

/* Compact layout: stack video and summary vertically */
.microlearning-view-edit__top-row--compact {
  flex-direction: column;
}

.microlearning-view-edit__top-row--compact .microlearning-view-edit__video-wrapper {
  max-width: 100%;
  width: 100%;
}

.microlearning-view-edit__top-row--compact .microlearning-view-edit__summary {
  max-width: 100%;
  width: 100%;
}

.microlearning-view-edit__summary :deep(.video-instructions-summary) {
  height: 100%;
}

.microlearning-view-edit__summary--static {
  pointer-events: none;
}

.microlearning-view-edit__summary--static :deep(.video-instructions-summary:hover) {
  background-color: #ffffff;
}

.microlearning-view-edit__summary--interactive {
  cursor: default;
}

.microlearning-view-edit__steps-container {
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
}

/* Custom scrollbar styling */
.microlearning-view-edit__steps-container::-webkit-scrollbar {
  width: 6px;
}

.microlearning-view-edit__steps-container::-webkit-scrollbar-track {
  background: #f2f4f7;
  border-radius: 3px;
}

.microlearning-view-edit__steps-container::-webkit-scrollbar-thumb {
  background: #d0d5dd;
  border-radius: 3px;
}

.microlearning-view-edit__steps-container::-webkit-scrollbar-thumb:hover {
  background: #98a2b3;
}

.microlearning-view-edit__steps-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
  width: 100%;
}

.microlearning-view-edit__step-card {
  width: 100%;
}

.microlearning-view-edit__step-card--static {
  pointer-events: none;
}

.microlearning-view-edit__step-card--static :deep(.instruction-step-card-timestamp-link) {
  pointer-events: auto;
}

.microlearning-view-edit__step-card--static :deep(.instruction-step-card-content-wrapper:hover) {
  background-color: transparent;
  cursor: default;
}

.microlearning-view-edit__step-card--interactive {
  cursor: url("../assets/cursorArrow.svg"), auto;
}

.microlearning-view-edit--video-only .microlearning-view-edit__video-container {
  max-width: 100%;
}

.microlearning-view-edit--with-error {
  gap: 22px;
}

/* Error state: side-by-side video + mismatch panel (override root column) */
.microlearning-view-edit--error-layout-row {
  flex-direction: row;
  align-items: stretch;
}

.microlearning-view-edit--error-layout-row .microlearning-view-edit__video-container {
  flex: 1;
  min-width: 0;
  max-width: calc(50% - 11px);
  display: flex;
  flex-direction: column;
}

.microlearning-view-edit--error-layout-row .microlearning-view-edit__iframe-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.microlearning-view-edit--error-layout-row .microlearning-view-edit__error-column {
  flex: 1;
  min-width: 0;
  max-width: calc(50% - 11px);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  align-self: stretch;
}

.microlearning-view-edit--error-layout-row .microlearning-view-edit__error-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.microlearning-view-edit--error-layout-row .microlearning-view-edit__error {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  width: 100%;
  display: flex;
  align-self: stretch;
}

.microlearning-view-edit--error-layout-row
  .microlearning-view-edit__error
  :deep(.video-instructions-invalid) {
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}
</style>
